import React, {type ReactNode} from 'react';
import {FloatingPortal} from '@floating-ui/react';

import {useOverlay} from '@packages/systems/ui/overlay';
import {ThemeProvider, SizeContextProvider} from '@packages/utilities/styled';

import type {HTMLAttributes, SpringTooling} from '../__SharedTypes';

type OverlayPortalProps = {
  children: ReactNode;
} & HTMLAttributes &
  SpringTooling;

/**
 * # OverlayPortal
 *
 * Creates an `Overlay` aware Portal for inserting floating ui components within our existing `Overlay` system.
 * If OverlayPortal is used within an `Overlay` context, it will insert the Portal there.
 * If not, the portal will be inserted at the bottom of `Body`.
 *
 * This component is for use internally within Spring.
 *
 * ## Usage
 *
 * ```jsx
 * import {OverlayPortal} from '@packages/systems/spring';
 * ```
 */
export const OverlayPortal = React.forwardRef<HTMLElement, OverlayPortalProps>(
  (
    {
      children,
      'data-automation-id': dataAutomationId = 'overlay',
      ...props
    }: OverlayPortalProps,
    ref
  ) => {
    const overlayContext = useOverlay();
    const root =
      overlayContext?.[overlayContext.length - 1]?.ref?.current ?? undefined;

    return (
      <FloatingPortal root={root}>
        <span
          {...props}
          data-automation-id={dataAutomationId}
          ref={ref}
          style={{display: 'contents'}}
        >
          <ThemeProvider>
            <SizeContextProvider>{children}</SizeContextProvider>
          </ThemeProvider>
        </span>
      </FloatingPortal>
    );
  }
);

OverlayPortal.displayName = 'OverlayPortal';

export default OverlayPortal;
